import { Anchor, Badge, Button, Group, SimpleGrid, Stack, Text, Title } from "@mantine/core";
import { IconBriefcase, IconBuilding, IconCalendar, IconFileTypePdf, IconMapPin, IconUserCheck } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { SubmitCandidateModal } from "../components/SubmitCandidateModal";
import { ShareIntakeLinkButton } from "../components/ShareIntakeLinkButton";
import { RecruiterJobEarnings } from "../components/RecruiterJobEarnings";
import { CandidateJdModal } from "../components/CandidateJdModal";
import { JobAdditionalDetails } from "../components/jobs/JobAdditionalDetails";
import { JobHiringFlow } from "../components/jobs/JobHiringFlow";
import { JobInsightsBanner } from "../components/jobs/JobInsightsBanner";
import { SubmissionPrepModal } from "../components/candidates/SubmissionPrepModal";
import { JobPoolMatchesSection } from "../components/matching/JobPoolMatchesSection";
import { ContentCard, LoadingOverlay } from "../components/ui";
import { candidatesApi, jobsApi, recruiterApi } from "../lib/api";
import { formatCities, formatDate, formatJobExperienceRange, formatPaise, SEGMENT_LABELS } from "../lib/format";
import { CONFIDENTIAL_CLIENT_NAME } from "../lib/kyc";

const KYC_PENDING_SUBMISSION_COPY = "KYC is under admin review. Browse jobs and pricing while you wait.";
const KYC_REQUIRED_SUBMISSION_COPY = "Complete KYC to see the employer and submit.";

export function JobDetailPage({ readOnly = false }: { readOnly?: boolean }) {
  const { jobId } = useParams<{ jobId: string }>();
  const [modalOpen, setModalOpen] = useState(false);
  const [poolCandidate, setPoolCandidate] = useState<{ id: string; name: string } | null>(null);
  const [jdOpen, setJdOpen] = useState(false);
  const [prepOpen, setPrepOpen] = useState(false);

  const jobQuery = useQuery({
    queryKey: ["job", jobId],
    queryFn: () => jobsApi.get(jobId!),
    enabled: !!jobId,
  });
  const profileQuery = useQuery({
    queryKey: ["my-profile"],
    queryFn: recruiterApi.myProfile,
    enabled: !readOnly,
  });
  const candidatesQuery = useQuery({
    queryKey: ["my-candidates", "for-prep"],
    queryFn: () => candidatesApi.list({ page_size: 100 }),
    enabled: !readOnly && prepOpen,
  });

  if (jobQuery.isLoading) {
    return <LoadingOverlay height="var(--axio-unit-300)" />;
  }

  const job = jobQuery.data;
  if (!job) return <Text c="dimmed">Job not found.</Text>;

  const kycVerified = profileQuery.data?.kyc_status === "verified";
  const canSubmit = !readOnly && job.status === "open" && kycVerified;
  const needsKycBeforeSubmit = !readOnly && job.status === "open" && !kycVerified;
  const companyMasked = Boolean(job.company_masked) || (!readOnly && profileQuery.data?.kyc_status !== "verified" && profileQuery.data != null);
  const showHiringFlow = !companyMasked && job.stages.length > 0;
  const experience = formatJobExperienceRange(job.experience_min, job.experience_max);
  const kycCtaLabel =
    profileQuery.data?.kyc_status === "submitted"
      ? "KYC approval pending"
      : profileQuery.data?.kyc_status === "rejected"
        ? "Fix KYC to submit"
        : "Complete KYC to see the employer and submit";

  return (
    <Stack gap="lg">
      <ContentCard>
        <Group justify="space-between" align="flex-start" wrap="wrap" gap="md">
          <Stack gap="xs">
            <Group gap="xs">
              <Badge color="gray" variant="outline" size="sm">
                {SEGMENT_LABELS[job.segment] ?? job.segment}
              </Badge>
              <Badge
                color={job.status === "open" ? "green" : "gray"}
                variant="outline"
                size="sm"

                tt="capitalize"
              >
                {job.status}
              </Badge>
            </Group>
            <Title order={2}>{job.title}</Title>
            <Group gap="var(--axio-unit-16)" c="dimmed" wrap="wrap">
              <Group gap="var(--axio-unit-4)">
                <IconBuilding size="var(--axio-unit-16)" />
                <Text size="sm">{companyMasked ? CONFIDENTIAL_CLIENT_NAME : job.company_name}</Text>
              </Group>
              <Group gap="var(--axio-unit-4)">
                <IconCalendar size="var(--axio-unit-16)" />
                <Text size="sm" className="tabular">
                  Posted {formatDate(job.published_at ?? job.created_at)}
                </Text>
              </Group>
              <Group gap="var(--axio-unit-4)">
                <IconMapPin size="var(--axio-unit-16)" />
                <Text size="sm">
                  {formatCities(job.cities)}
                  {job.is_remote ? " · Remote OK" : ""}
                </Text>
              </Group>
              <Group gap="var(--axio-unit-4)">
                <IconBriefcase size="var(--axio-unit-16)" />
                <Text size="sm">
                  {job.openings_total - job.openings_filled} of {job.openings_total} openings left
                </Text>
              </Group>
            </Group>
          </Stack>
          <Group gap="xs">
            {!readOnly && jobId && <ShareIntakeLinkButton size="sm" />}
            {!readOnly && canSubmit && (
              <Button variant="default" leftSection={<IconFileTypePdf size="var(--axio-unit-16)" />} onClick={() => setJdOpen(true)}>
                Create candidate JD
              </Button>
            )}
            {!readOnly && canSubmit && (
              <Button variant="default" onClick={() => setPrepOpen(true)}>
                Prep a candidate
              </Button>
            )}
            {!readOnly && canSubmit && (
            <Button
              variant="filled"
              color="violet"
              size="md"
              onClick={() => setModalOpen(true)}
              data-testid="open-submit-candidate"
            >
              Submit a Candidate
            </Button>
            )}
            {needsKycBeforeSubmit && (
              <Button component={Link} to="/kyc" variant="filled" color="violet" size="md">
                {kycCtaLabel}
              </Button>
            )}
          </Group>
        </Group>
      </ContentCard>

      {!readOnly && jobId ? <JobInsightsBanner jobId={jobId} /> : null}

      {!readOnly && jobId ? (
        <JobPoolMatchesSection
          jobId={jobId}
          onSubmit={(candidateId, candidateName) => {
            setPoolCandidate({ id: candidateId, name: candidateName });
            setModalOpen(true);
          }}
        />
      ) : null}

      {needsKycBeforeSubmit && (
        <Text size="sm" c="yellow.8">
          {profileQuery.data?.kyc_status === "submitted"
            ? KYC_PENDING_SUBMISSION_COPY
            : KYC_REQUIRED_SUBMISSION_COPY} 
          <Anchor component={Link} to="/kyc" fw="var(--axio-font-weight-semibold)">
            {profileQuery.data?.kyc_status === "submitted" ? "View KYC status" : "Open KYC"}
          </Anchor>
        </Text>
      )}

      <SimpleGrid cols={{ base: 1, sm: experience ? 3 : 2 }}>
        <ContentCard>
          <Text size="xs" c="dimmed" fw="var(--axio-font-weight-semibold)">
            CTC band
          </Text>
          <Text fw="var(--axio-font-weight-semibold)" mt="var(--axio-unit-4)" className="tabular">
            {formatPaise(job.ctc_min_paise)} – {formatPaise(job.ctc_max_paise)}
          </Text>
        </ContentCard>
        {experience && (
          <ContentCard>
            <Text size="xs" c="dimmed" fw="var(--axio-font-weight-semibold)">
              Experience
            </Text>
            <Group gap="var(--axio-unit-6)" mt="var(--axio-unit-4)">
              <IconUserCheck size="var(--axio-unit-16)" color="gray" />
              <Text fw="var(--axio-font-weight-semibold)">{experience}</Text>
            </Group>
          </ContentCard>
        )}
        <ContentCard>
          <Text size="xs" c="dimmed" fw="var(--axio-font-weight-semibold)">
            Guarantee window
          </Text>
          <Text fw="var(--axio-font-weight-semibold)" mt="var(--axio-unit-4)" className="tabular">
            {job.guarantee_days} days
          </Text>
        </ContentCard>
      </SimpleGrid>

      <JobAdditionalDetails job={job} audience="recruiter" />

      <RecruiterJobEarnings job={job} />

      {job.description && (
        <ContentCard>
          <Title order={5} mb="xs">
            Job Description
          </Title>
          <Text style={{ whiteSpace: "pre-wrap" }}>{job.description}</Text>
        </ContentCard>
      )}

      {job.requirements && (
        <ContentCard>
          <Title order={5} mb="xs">
            Requirements
          </Title>
          <Text style={{ whiteSpace: "pre-wrap" }}>{job.requirements}</Text>
        </ContentCard>
      )}

      {showHiringFlow ? <JobHiringFlow stages={job.stages} caption={readOnly ? "readOnly" : "recruiter"} /> : null}

      {!readOnly && jobId && (
        <SubmitCandidateModal
          jobId={jobId}
          opened={modalOpen}
          candidateId={poolCandidate?.id}
          candidateName={poolCandidate?.name}
          onClose={() => {
            setModalOpen(false);
            setPoolCandidate(null);
          }}
        />
      )}
      {!readOnly && jobId && <CandidateJdModal jobId={jobId} opened={jdOpen} onClose={() => setJdOpen(false)} />}
      {!readOnly && jobId && (
        <SubmissionPrepModal
          opened={prepOpen}
          onClose={() => setPrepOpen(false)}
          jobId={jobId}
          candidateOptions={(candidatesQuery.data?.items ?? []).map((c) => ({
            value: c.id,
            label: `${c.full_name} — ${c.phone}`,
          }))}
        />
      )}
    </Stack>
  );
}
